import React, { useState, useMemo, useEffect } from 'react';
import { Clock } from 'lucide-react';

type Props = {
	thinking: string;
	isStreaming: boolean;
	initialExpanded?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
};

const ROTATE_INTERVAL = 2000;

const extractSentences = (text: string): string[] => {
	const raw = text
		.split(/(?<=[.!?])\s+|\n+/)
		.map(s => s.trim())
		.filter(s => s.length > 10);
	return raw.length > 0 ? raw : [text.slice(0, 120)];
};

const ThinkingBlock = ({ thinking, isStreaming, initialExpanded, onExpandedChange }: Props) => {
	const [expanded, setExpanded] = useState(initialExpanded ?? false);
	const [visibleIdx, setVisibleIdx] = useState(0);

	const sentences = useMemo(() => extractSentences(thinking), [thinking]);

	useEffect(() => {
		if (!isStreaming) return;
		const id = setInterval(() => {
			setVisibleIdx(prev => {
				const next = prev + 1;
				return next < sentences.length ? next : prev;
			});
		}, ROTATE_INTERVAL);
		return () => clearInterval(id);
	}, [isStreaming, sentences.length]);

	useEffect(() => {
		setVisibleIdx(prev => Math.min(prev, Math.max(0, sentences.length - 1)));
	}, [sentences.length]);

	const displaySentence = sentences[visibleIdx] || sentences[sentences.length - 1] || '';

	const toggle = () => {
		setExpanded(v => {
			const next = !v;
			onExpandedChange?.(next);
			return next;
		});
	};

	return (
		<div className="pr-thinking-sep">
			<div className="pr-thinking-sep-track">
				<div className="pr-thinking-sep-icon">
					<Clock style={{ width: 14, height: 14 }} />
				</div>
				<div className="pr-thinking-sep-line" />
			</div>
			<div className="pr-thinking-sep-body" onClick={toggle}>
				{!expanded && (
					<div className="pr-thinking-sep-title" key={isStreaming ? visibleIdx : 'final'}>
						{displaySentence}
					</div>
				)}
				{expanded && <div className="pr-thinking-sep-full">{thinking}</div>}
			</div>
		</div>
	);
};

export default ThinkingBlock;
